import { notify, useModal } from '@billy/design-system'
import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { VatDeclarationsPeriod } from '@features/vatDeclarations/types/vatDeclarationsPeriod'
import {
  UpdateOrganizationProps,
  useUpdateOrganization,
  useUserOrganization,
} from '@modules-deprecated/app/organization'

import { queryClient } from '../../../../../config/queryClient'
import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { useSegment } from '../../../../../hooks'
import { APIError } from '../../../../../utils'
import { useUpdateVatDeclarationSettlement } from '../../../hooks/useUpdateVatDeclarationSettlement'
import { getPeriodData, PeriodData } from '../../../utils/getPeriodData'

interface ContextState {
  closeModal: () => void
  isCorrection: boolean
  isProcessing: boolean
  periodEndDate: string
  periodData: PeriodData | undefined
  periodText: string
  settleVatDeclarationsPeriod: () => void
  toggleLockAccounting: () => void
}

const VatDeclarationsSettlePeriodModalContext = createContext<ContextState | undefined>(undefined)

interface VatDeclarationsSettlePeriodModalContextProps {
  children?: ReactNode
  modalId: string
  vatDeclaration: VatDeclarationsPeriod
}

export const VatDeclarationsSettlePeriodModalContextProvider = ({
  children,
  modalId,
  vatDeclaration,
}: VatDeclarationsSettlePeriodModalContextProps): ReactElement => {
  const { endDate, corrections, id, period, periodText } = vatDeclaration

  const [isAccountingPeriodLocked, setIsAccountingPeriodLocked] = useState(false)
  const { t } = useTranslation()
  const { track } = useSegment()
  const { organization } = useUserOrganization()
  const { close: closeModal } = useModal(modalId)

  const periodData = getPeriodData(period || '')
  const currentCorrection = corrections?.find(({ salesTaxReturnId }) => salesTaxReturnId === id)
  const isCorrection = (currentCorrection?.correctionNo || 0) > 0

  const { update: updateOrganizationLockDate, isProcessing: isProcessingLockDate } = useUpdateOrganization({
    onError: (error: APIError | undefined) => {
      notify({
        variant: 'error',
        message: error?.message || t('vat_declarations.settlement_modal.locking.fail'),
        id: NotificationKeys.VatDeclarationsSettle,
      })
    },
  })

  const { update: updatePeriodSettlement, isProcessing: isProcessingSettlement } = useUpdateVatDeclarationSettlement({
    onError: (error: APIError | undefined) => {
      closeModal()
      notify({
        variant: 'error',
        message: error?.message || t('vat_declarations.settlement_modal.fail'),
        id: NotificationKeys.VatDeclarationsSettle,
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKeys.VatDeclarations)
      queryClient.invalidateQueries(QueryKeys.TaxReturnPeriods)
      closeModal()
      notify({
        variant: 'success',
        message: t('vat_declarations.settlement_modal.success'),
        id: NotificationKeys.VatDeclarationsSettle,
      })

      if (currentCorrection && periodData) {
        track('VAT Declaration Submitted (Client)', {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          period_settled: periodData.periodSettled,
          year: periodData.year,
          context: 'manual',
          type: isCorrection ? 'correction' : 'normal',
        })
      }
    },
  })

  const handleModalClose = useCallback(() => {
    closeModal()
  }, [closeModal])

  const toggleLockAccounting = useCallback(() => {
    setIsAccountingPeriodLocked((prevState: boolean) => !prevState)
  }, [setIsAccountingPeriodLocked])

  const settleVatDeclarationsPeriod = useCallback(() => {
    if (isAccountingPeriodLocked && organization?.id) {
      const updateOrganizationLockDatePayload: UpdateOrganizationProps = {
        organizationId: organization.id,
        payload: {
          fiscalLockingDate: endDate,
        },
      }

      updateOrganizationLockDate(updateOrganizationLockDatePayload)
    }

    updatePeriodSettlement({ id, isSettled: true })
  }, [endDate, id, isAccountingPeriodLocked, organization?.id, updateOrganizationLockDate, updatePeriodSettlement])

  return (
    <VatDeclarationsSettlePeriodModalContext.Provider
      value={{
        closeModal: handleModalClose,
        isCorrection,
        isProcessing: isProcessingLockDate || isProcessingSettlement,
        periodEndDate: endDate,
        periodData,
        periodText,
        settleVatDeclarationsPeriod,
        toggleLockAccounting,
      }}
    >
      {children}
    </VatDeclarationsSettlePeriodModalContext.Provider>
  )
}

export const useVatDeclarationsSettlePeriodModal = () => {
  const context = useContext(VatDeclarationsSettlePeriodModalContext)

  if (!context) {
    throw new Error('VatDeclarationsSettlePeriodModalContext is missing in the module!')
  }

  return context
}
